import { apiRequest } from './client.js';

/**
 * Storage Configuration (M01 §13 File Storage Framework, T007) —
 * real /v1/storage contract. Credentials are write-only (never returned).
 */

export const STORAGE_PROVIDERS = ['S3', 'AZURE_BLOB', 'GCS', 'LOCAL'] as const;
export type StorageProvider = (typeof STORAGE_PROVIDERS)[number];

export const STORAGE_PROVIDER_LABELS: Readonly<Record<string, string>> = {
  S3: 'AWS S3',
  AZURE_BLOB: 'Azure Blob',
  GCS: 'Google Cloud Storage',
  LOCAL: 'Local disk',
};

export const STORAGE_ACLS = ['PRIVATE', 'READ'] as const;
export type StorageAcl = (typeof STORAGE_ACLS)[number];

/** T007 storage profile (credentials never returned). */
export interface StorageProfile {
  readonly id: string;
  readonly profileCode: string;
  readonly scope: 'GLOBAL' | 'TENANT';
  readonly tenantId?: string | null;
  readonly provider: StorageProvider;
  readonly region?: string | null;
  readonly bucketOrContainer: string;
  readonly basePrefix?: string | null;
  readonly defaultAcl: StorageAcl;
  readonly recordStatusCode?: string;
  readonly version: number;
}

export interface CreateStorageInput {
  readonly profileCode: string;
  readonly provider: StorageProvider;
  readonly region?: string;
  readonly bucketOrContainer: string;
  readonly basePrefix?: string;
  readonly defaultAcl?: StorageAcl;
  /** base64-encoded credential blob. */
  readonly credentialsBase64: string;
}

export interface UpdateStorageInput {
  readonly bucketOrContainer?: string;
  readonly basePrefix?: string;
  readonly defaultAcl?: StorageAcl;
  readonly credentialsBase64?: string;
  readonly version: number;
}

type Tok = string | null | undefined;

/** GET /v1/storage — active storage profiles for the current tenant. */
export async function listStorageProfiles(
  token?: Tok,
): Promise<readonly StorageProfile[]> {
  const r = await apiRequest<{ items: readonly StorageProfile[] } | readonly StorageProfile[]>(
    '/storage',
    { token },
  );
  if (!r) return [];
  return Array.isArray(r)
    ? r
    : (r as { items: readonly StorageProfile[] }).items ?? [];
}

/** GET /v1/storage/:id — a single storage profile. */
export function getStorageProfile(
  id: string,
  token?: Tok,
): Promise<StorageProfile> {
  return apiRequest<StorageProfile>(`/storage/${encodeURIComponent(id)}`, {
    token,
  });
}

/** POST /v1/storage — create a tenant-scoped storage profile. */
export function createStorageProfile(
  body: CreateStorageInput,
  token?: Tok,
): Promise<StorageProfile> {
  return apiRequest<StorageProfile>('/storage', {
    method: 'POST',
    token,
    body,
  });
}

/** PATCH /v1/storage/:id — update a storage profile. */
export function updateStorageProfile(
  id: string,
  body: UpdateStorageInput,
  token?: Tok,
): Promise<StorageProfile> {
  return apiRequest<StorageProfile>(`/storage/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    token,
    body,
  });
}

/** POST /v1/storage/:id/deactivate — soft-delete a storage profile. */
export function deactivateStorageProfile(
  id: string,
  version: number,
  token?: Tok,
): Promise<{ id: string; recordStatusCode: string }> {
  return apiRequest<{ id: string; recordStatusCode: string }>(
    `/storage/${encodeURIComponent(id)}/deactivate`,
    { method: 'POST', token, body: { version } },
  );
}
